import React, { useEffect, useState } from "react";
import { Card, Button, Row, Col, Input, Tag, Space, Select, DatePicker, Modal, Table, Divider } from "antd";
import { DataGrid, GridToolbar } from "@mui/x-data-grid";
import { useNavigate } from "react-router-dom";
import {
  ArrowLeftOutlined,
  SearchOutlined,
  UserOutlined,
  ShopOutlined,
  TransactionOutlined,
  CheckCircleOutlined,
  UserDeleteOutlined,
  DollarOutlined,
  FundOutlined,
  AppstoreOutlined,
  EyeOutlined,
} from "@ant-design/icons";
import noDataLogo from "../../assets/images/no-data.png";
import moment from "moment";
import { useMatStyles } from "./style";
import { hideLoader, showLoader } from "../../utils/loader";
import { useTheme } from "styled-components";
import ExportData from "../WayaOfficial/modal/exportData";
import GeneratedQr from "./generatedQrModal";
import {
  getMarketerSummary,
  getMarketerCustomers,
  getMarketerTransactionStats,
  getCustomerTransactionCategories,
} from "../../services/referral/marketerService";

const { RangePicker } = DatePicker;

const ManageMarketer = ({ setShowManageMarkert, selectedMarketer }) => {
  const theme = useTheme();
  const styles = useMatStyles({ theme });
  const navigate = useNavigate();
  const [showExport, setShowExport] = useState(false);
  const [pagination, setPagination] = useState({
    current: 0,
    pageSize: 10,
    total: 0,
  });
  const [search, setSearch] = useState({
    searchText: "",
    type: "ALL",
    productCategory: "ALL",
    fromDate: "",
    toDate: "",
    sortDir: "desc",
  });

  const [allTransactions, setAllTransactions] = useState([]);
  const [summaryData, setSummaryData] = useState(null);
  const [transStats, setTransStats] = useState(null);
  const [selectedRow, setSelectedRow] = useState({});
  const [qrData, setQrData] = useState([]);
  const [openModal, setOpenModal] = useState(false);
  const [loading, setLoading] = useState(false);

  // Customer Products Modal State
  const [showProductsModal, setShowProductsModal] = useState(false);
  const [selectedCustomerForProducts, setSelectedCustomerForProducts] = useState(null);
  const [customerProducts, setCustomerProducts] = useState([]);
  const [productsLoading, setProductsLoading] = useState(false);

  const supportCode = selectedMarketer?.supportCode || selectedMarketer?.referralCode;

  const fetchSummaryAndStats = async (overrideFrom, overrideTo) => {
    if (!supportCode) return;
    try {
      const dateParams = {
        pageNo: 0,
        pageSize: 20,
      };
      const f = overrideFrom !== undefined ? overrideFrom : search.fromDate;
      const t = overrideTo !== undefined ? overrideTo : search.toDate;
      if (f) dateParams.from = f;
      if (t) dateParams.to = t;

      const summaryRes = await getMarketerSummary(supportCode, dateParams);
      const sData = summaryRes?.data?.data || summaryRes?.data || summaryRes;
      if (sData) setSummaryData(sData);

      const statsRes = await getMarketerTransactionStats(supportCode, dateParams);
      const stData = statsRes?.data?.data || statsRes?.data || statsRes;
      if (stData) setTransStats(stData);
    } catch (err) {
      console.error("Error fetching marketer summary/stats:", err);
    }
  };

  const getTransactions = async (page = 0, size = pagination.pageSize) => {
    showLoader();
    setLoading(true);
    try {
      const codeToUse = supportCode || selectedMarketer?.supportCode || selectedMarketer?.referralCode;
      if (!codeToUse) {
        setAllTransactions([]);
        setPagination({ current: page, pageSize: size, total: 0 });
        hideLoader();
        setLoading(false);
        return;
      }

      const params = {
        pageNo: page,
        pageSize: size,
      };
      if (search.searchText) params.query = search.searchText;
      if (search.type && search.type !== "ALL") params.type = search.type;
      if (search.productCategory && search.productCategory !== "ALL") params.category = search.productCategory;
      if (search.fromDate) params.from = search.fromDate;
      if (search.toDate) params.to = search.toDate;
      if (search.sortDir) params.sortDir = search.sortDir;

      const res = await getMarketerCustomers(codeToUse, params);
      const resData = res?.data?.data || res?.data || res;
      const customersList =
        resData?.customers || resData?.perCustomer || resData?.content || (Array.isArray(resData) ? resData : []);
      const total = resData?.totalElements ?? customersList.length;

      const formatted = customersList.map((item, idx) => ({
        ...item,
        id: item.customerProfileId || item.id || item.userId || `cust_${idx}`,
        name: item.name || item.fullName || `${item.firstName || ''} ${item.lastName || ''}`.trim() || 'N/A',
        email: item.email || 'N/A',
        phoneNumber: item.phoneNumber || item.phone || 'N/A',
        accountNumber: item.accountNumber || '—',
        organizationName: item.organizationName || '—',
        referenceCode: item.referenceCode || '—',
        customerCategory: item.customerCategory || item.category || 'GENERAL',
        type: item.type || (item.corporate ? 'CORPORATE' : 'PERSONAL'),
        transactionCount: item.transactionCount ?? 0,
        totalTransactionAmount: item.totalTransactionAmount ?? item.totalAmount ?? 0,
        revenue: item.revenue ?? 0,
        profit: item.profit ?? 0,
        commissionPaid: item.commissionPaid ?? 0,
        registeredAt: item.registeredAt || item.dateAssigned || item.createdAt || item.dateCreated,
        dateAssigned: item.registeredAt || item.dateAssigned || item.createdAt,
      }));

      setAllTransactions(formatted);
      setPagination({ current: page, pageSize: size, total });
    } catch (err) {
      console.error('Error loading marketer assigned customers:', err);
    } finally {
      hideLoader();
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchSummaryAndStats();
    getTransactions(0, pagination.pageSize);
  }, [supportCode]);

  const modalCloseAction = () => {
    setOpenModal(false);
  };

  const handleOpenProductsModal = async (customerRow, overrideFrom, overrideTo) => {
    setSelectedCustomerForProducts(customerRow);
    setShowProductsModal(true);
    setProductsLoading(true);
    try {
      const targetUserId = customerRow.userId || customerRow.customerUserId || customerRow.id;
      const dateParams = {};
      const f = overrideFrom !== undefined ? overrideFrom : search.fromDate;
      const t = overrideTo !== undefined ? overrideTo : search.toDate;
      if (f) dateParams.from = f;
      if (t) dateParams.to = t;

      const res = await getCustomerTransactionCategories(targetUserId, dateParams);
      const resData = res?.data?.data || res?.data || res;
      const cats = resData?.categoryBreakdown || resData?.categories || resData?.content || (Array.isArray(resData) ? resData : []);
      setCustomerProducts(Array.isArray(cats) ? cats : []);
    } catch (err) {
      console.error("Error fetching customer product breakdown:", err);
      setCustomerProducts([]);
    } finally {
      setProductsLoading(false);
    }
  };

  const handleChangeRowsPerPage = async (value) => {
    setPagination({ ...pagination, current: 0, pageSize: value });
    await getTransactions(0, value);
  };

  const handleChangePage = async (newPage) => {
    setPagination({ ...pagination, current: newPage });
    await getTransactions(newPage, pagination.pageSize);
  };

  const columns = [
    {
      field: 'name',
      headerName: 'Full Name',
      width: 180,
      headerClassName: styles.tableHeader,
    },
    {
      field: 'email',
      headerName: 'Email',
      width: 200,
      headerClassName: styles.tableHeader,
    },
    {
      field: 'phoneNumber',
      headerName: 'Phone',
      width: 140,
      headerClassName: styles.tableHeader,
      renderCell: ({ row }) => row.phoneNumber || '—',
    },
    {
      field: 'type',
      headerName: 'Type',
      width: 120,
      headerClassName: styles.tableHeader,
      renderCell: ({ row }) => {
        const val = (row.type || '').toUpperCase();
        let tagColor = 'blue';
        if (val === 'CORPORATE' || val === 'MERCHANT') tagColor = 'purple';
        if (val === 'PERSONAL' || val === 'CUSTOMER') tagColor = 'green';
        return <Tag color={tagColor}>{val || 'CUSTOMER'}</Tag>;
      },
    },
    {
      field: 'accountNumber',
      headerName: 'Account Number',
      width: 140,
      headerClassName: styles.tableHeader,
      renderCell: ({ row }) => row.accountNumber || '—',
    },
    {
      field: 'customerCategory',
      headerName: 'Products / Category',
      width: 160,
      headerClassName: styles.tableHeader,
      renderCell: ({ row }) => (
        <Space>
          <Tag color="volcano">{row.customerCategory || 'GENERAL'}</Tag>
          <Button
            size="small"
            type="text"
            icon={<EyeOutlined />}
            onClick={() => handleOpenProductsModal(row)}
            title="View Product Breakdown"
          />
        </Space>
      ),
    },
    {
      field: 'transactionCount',
      headerName: 'Tx Count',
      width: 110,
      headerClassName: styles.tableHeader,
      renderCell: ({ row }) => row.transactionCount ?? row.txCount ?? 0,
    },
    {
      field: 'totalTransactionAmount',
      headerName: 'Total Volume',
      width: 150,
      headerClassName: styles.tableHeader,
      renderCell: ({ row }) => {
        const amt = row.totalTransactionAmount ?? row.totalVolume ?? row.totalAmount;
        return amt != null ? `₦${Number(amt).toLocaleString(undefined, { minimumFractionDigits: 2 })}` : '—';
      },
    },
    {
      field: 'revenue',
      headerName: 'Revenue (Fee)',
      width: 150,
      headerClassName: styles.tableHeader,
      renderCell: ({ row }) => {
        const rev = row.revenue;
        return rev != null ? `₦${Number(rev).toLocaleString(undefined, { minimumFractionDigits: 2 })}` : '₦0.00';
      },
    },
    {
      field: 'profit',
      headerName: 'Profit (Net)',
      width: 140,
      headerClassName: styles.tableHeader,
      renderCell: ({ row }) => {
        const prf = row.profit;
        return prf != null ? `₦${Number(prf).toLocaleString(undefined, { minimumFractionDigits: 2 })}` : '₦0.00';
      },
    },
    {
      field: 'commissionPaid',
      headerName: 'Commission Paid',
      width: 150,
      headerClassName: styles.tableHeader,
      renderCell: ({ row }) => {
        const comm = row.commissionPaid;
        return comm != null ? `₦${Number(comm).toLocaleString(undefined, { minimumFractionDigits: 2 })}` : '₦0.00';
      },
    },
    {
      field: 'registeredAt',
      headerName: 'Date Registered',
      width: 170,
      headerClassName: styles.tableHeader,
      renderCell: ({ row }) => (row.registeredAt ? moment(row.registeredAt).format('LLL') : '—'),
    },
  ];

  const headline = summaryData?.headline || {};
  const totals = summaryData?.totals || transStats?.totals || {};

  const totalCustomersOnboarded =
    headline.customersOnboarded ??
    totals.customersOnboarded ??
    summaryData?.totalCustomers ??
    summaryData?.totalOnboarded ??
    transStats?.totalCustomers ??
    allTransactions?.length ??
    0;

  const corporateOnboarded = headline.corporateOnboarded ?? 0;
  const personalOnboarded = headline.personalOnboarded ?? 0;

  const totalVolume =
    headline.totalTransactionAmount ??
    totals.totalAmount ??
    summaryData?.totalVolume ??
    transStats?.totalVolume ??
    0;

  const totalTransactionsCount =
    headline.transactionCount ??
    totals.transactionCount ??
    summaryData?.totalTransactions ??
    transStats?.totalTransactions ??
    0;

  const customersTransacting = headline.customersTransacting ?? totals.customersTransacting ?? 0;
  const dormantCustomers = headline.dormantCustomers ?? totals.dormantCustomers ?? 0;
  const totalRevenue = totals.revenue ?? 0;
  const totalProfit = totals.profit ?? 0;

  const categoryBreakdown = summaryData?.categoryBreakdown || transStats?.categoryBreakdown || [];

  const formatNaira = (val) =>
    val != null ? `₦${Number(val).toLocaleString(undefined, { minimumFractionDigits: 2 })}` : '₦0.00';

  const productCategoryColumns = [
    {
      title: 'Product / Category Name',
      dataIndex: 'category',
      key: 'category',
      render: (val) => <Tag color="blue">{val || 'GENERAL'}</Tag>,
    },
    {
      title: 'Transaction Count',
      dataIndex: 'transactionCount',
      key: 'transactionCount',
      render: (val, row) => val ?? row.count ?? 0,
    },
    {
      title: 'Total Volume',
      dataIndex: 'totalAmount',
      key: 'totalAmount',
      render: (val, row) => formatNaira(val ?? row.volume),
    },
    {
      title: 'Total Charge',
      dataIndex: 'totalCharge',
      key: 'totalCharge',
      render: (val) => formatNaira(val),
    },
    {
      title: 'Revenue (Fee)',
      dataIndex: 'revenue',
      key: 'revenue',
      render: (val) => formatNaira(val),
    },
    {
      title: 'Commission Paid',
      dataIndex: 'commissionPaid',
      key: 'commissionPaid',
      render: (val) => formatNaira(val),
    },
    {
      title: 'Profit',
      dataIndex: 'profit',
      key: 'profit',
      render: (val) => formatNaira(val),
    },
  ];

  return (
    <>
      <div style={{ display: "flex", alignItems: "center", justifyContent: "space-between", marginBottom: "20px" }}>
        <div
          style={{ cursor: "pointer", display: "flex", alignItems: "center", gap: 8, fontSize: "16px", fontWeight: "600", color: "#FF4B01" }}
          onClick={() => setShowManageMarkert(false)}
        >
          <ArrowLeftOutlined />
          <span>Back to Marketers</span>
        </div>
        <div style={{ textAlign: "right" }}>
          <h3 style={{ margin: 0, fontWeight: "700" }}>
            Marketer: {selectedMarketer?.fullName || selectedMarketer?.name || summaryData?.marketer?.name || selectedMarketer?.marketerUserEmail}
          </h3>
          <Space wrap style={{ marginTop: 4 }}>
            <span style={{ fontSize: "12px", color: "#666" }}>{selectedMarketer?.marketerUserEmail || summaryData?.marketer?.email}</span>
            {supportCode && <Tag color="volcano">Code: {supportCode}</Tag>}
            {selectedMarketer?.phoneNumber && <Tag color="blue">Phone: {selectedMarketer.phoneNumber}</Tag>}
            {corporateOnboarded > 0 && <Tag color="purple">Corporate: {corporateOnboarded}</Tag>}
            {personalOnboarded > 0 && <Tag color="cyan">Personal: {personalOnboarded}</Tag>}
          </Space>
        </div>
      </div>

      {/* Summary Cards */}
      <Row gutter={[16, 16]} style={{ marginBottom: "24px" }}>
        <Col xs={24} sm={12} md={4}>
          <Card
            style={{
              borderRadius: "12px",
              boxShadow: "0 4px 12px rgba(0, 0, 0, 0.05)",
              border: "1px solid #FFEAE1",
              background: "linear-gradient(135deg, #FFF6F3 0%, #FFFFFF 100%)",
            }}
          >
            <div style={{ display: "flex", alignItems: "center", justifyContent: "space-between" }}>
              <div>
                <p style={{ margin: 0, fontSize: "12px", color: "#666", fontWeight: "500" }}>Total Onboarded</p>
                <h2 style={{ margin: "6px 0 0 0", fontSize: "22px", fontWeight: "800", color: "#FF4B01" }}>
                  {totalCustomersOnboarded}
                </h2>
              </div>
              <div style={{ background: "#FFEAE1", padding: "8px", borderRadius: "50%", display: "flex", alignItems: "center", justifyContent: "center" }}>
                <UserOutlined style={{ fontSize: "18px", color: "#FF4B01" }} />
              </div>
            </div>
          </Card>
        </Col>

        <Col xs={24} sm={12} md={4}>
          <Card
            style={{
              borderRadius: "12px",
              boxShadow: "0 4px 12px rgba(0, 0, 0, 0.05)",
              border: "1px solid #E6F7FF",
              background: "linear-gradient(135deg, #E6F7FF 0%, #FFFFFF 100%)",
            }}
          >
            <div style={{ display: "flex", alignItems: "center", justifyContent: "space-between" }}>
              <div>
                <p style={{ margin: 0, fontSize: "12px", color: "#666", fontWeight: "500" }}>Total Volume</p>
                <h2 style={{ margin: "6px 0 0 0", fontSize: "18px", fontWeight: "800", color: "#1890ff" }}>
                  ₦{Number(totalVolume).toLocaleString(undefined, { minimumFractionDigits: 2 })}
                </h2>
              </div>
              <div style={{ background: "#BAE7FF", padding: "8px", borderRadius: "50%", display: "flex", alignItems: "center", justifyContent: "center" }}>
                <TransactionOutlined style={{ fontSize: "18px", color: "#1890ff" }} />
              </div>
            </div>
          </Card>
        </Col>

        <Col xs={24} sm={12} md={4}>
          <Card
            style={{
              borderRadius: "12px",
              boxShadow: "0 4px 12px rgba(0, 0, 0, 0.05)",
              border: "1px solid #F6FFED",
              background: "linear-gradient(135deg, #F6FFED 0%, #FFFFFF 100%)",
            }}
          >
            <div style={{ display: "flex", alignItems: "center", justifyContent: "space-between" }}>
              <div>
                <p style={{ margin: 0, fontSize: "12px", color: "#666", fontWeight: "500" }}>Transactions</p>
                <h2 style={{ margin: "6px 0 0 0", fontSize: "22px", fontWeight: "800", color: "#52c41a" }}>
                  {totalTransactionsCount}
                </h2>
              </div>
              <div style={{ background: "#D9F7BE", padding: "8px", borderRadius: "50%", display: "flex", alignItems: "center", justifyContent: "center" }}>
                <ShopOutlined style={{ fontSize: "18px", color: "#52c41a" }} />
              </div>
            </div>
          </Card>
        </Col>

        <Col xs={24} sm={12} md={4}>
          <Card
            style={{
              borderRadius: "12px",
              boxShadow: "0 4px 12px rgba(0, 0, 0, 0.05)",
              border: "1px solid #FFF7E6",
              background: "linear-gradient(135deg, #FFF7E6 0%, #FFFFFF 100%)",
            }}
          >
            <div style={{ display: "flex", alignItems: "center", justifyContent: "space-between" }}>
              <div>
                <p style={{ margin: 0, fontSize: "12px", color: "#666", fontWeight: "500" }}>Total Revenue (Fee)</p>
                <h2 style={{ margin: "6px 0 0 0", fontSize: "18px", fontWeight: "800", color: "#fa8c16" }}>
                  ₦{Number(totalRevenue).toLocaleString(undefined, { minimumFractionDigits: 2 })}
                </h2>
              </div>
              <div style={{ background: "#FFE7BA", padding: "8px", borderRadius: "50%", display: "flex", alignItems: "center", justifyContent: "center" }}>
                <DollarOutlined style={{ fontSize: "18px", color: "#fa8c16" }} />
              </div>
            </div>
          </Card>
        </Col>

        <Col xs={24} sm={12} md={4}>
          <Card
            style={{
              borderRadius: "12px",
              boxShadow: "0 4px 12px rgba(0, 0, 0, 0.05)",
              border: "1px solid #F9F0FF",
              background: "linear-gradient(135deg, #F9F0FF 0%, #FFFFFF 100%)",
            }}
          >
            <div style={{ display: "flex", alignItems: "center", justifyContent: "space-between" }}>
              <div>
                <p style={{ margin: 0, fontSize: "12px", color: "#666", fontWeight: "500" }}>Total Profit (Net)</p>
                <h2 style={{ margin: "6px 0 0 0", fontSize: "18px", fontWeight: "800", color: "#722ed1" }}>
                  ₦{Number(totalProfit).toLocaleString(undefined, { minimumFractionDigits: 2 })}
                </h2>
              </div>
              <div style={{ background: "#EFDBFF", padding: "8px", borderRadius: "50%", display: "flex", alignItems: "center", justifyContent: "center" }}>
                <FundOutlined style={{ fontSize: "18px", color: "#722ed1" }} />
              </div>
            </div>
          </Card>
        </Col>

        <Col xs={24} sm={12} md={4}>
          <Card
            style={{
              borderRadius: "12px",
              boxShadow: "0 4px 12px rgba(0, 0, 0, 0.05)",
              border: "1px solid #E6FFFB",
              background: "linear-gradient(135deg, #E6FFFB 0%, #FFFFFF 100%)",
            }}
          >
            <div style={{ display: "flex", alignItems: "center", justifyContent: "space-between" }}>
              <div>
                <p style={{ margin: 0, fontSize: "12px", color: "#666", fontWeight: "500" }}>Active / Transacting</p>
                <h2 style={{ margin: "6px 0 0 0", fontSize: "22px", fontWeight: "800", color: "#13c2c2" }}>
                  {customersTransacting} / <span style={{ color: '#eb2f96', fontSize: '18px' }}>{dormantCustomers} Dormant</span>
                </h2>
              </div>
              <div style={{ background: "#B5F5EC", padding: "8px", borderRadius: "50%", display: "flex", alignItems: "center", justifyContent: "center" }}>
                <CheckCircleOutlined style={{ fontSize: "18px", color: "#13c2c2" }} />
              </div>
            </div>
          </Card>
        </Col>
      </Row>

      {/* Category / Products Breakdown */}
      {Array.isArray(categoryBreakdown) && categoryBreakdown.length > 0 && (
        <Card size="small" title="Transaction Products & Categories Breakdown" style={{ marginBottom: "20px", borderRadius: "8px" }}>
          <Space wrap size="middle">
            {categoryBreakdown.map((cat, idx) => {
              const txCount = cat.transactionCount ?? cat.count ?? 0;
              const volume = cat.totalAmount ?? cat.volume ?? 0;
              const profit = cat.profit ?? 0;
              return (
                <Tag key={idx} color="blue" style={{ fontSize: "12px", padding: "6px 12px", borderRadius: "6px" }}>
                  <AppstoreOutlined style={{ marginRight: 4 }} />
                  {cat.category || cat.name || 'GENERAL'}: <strong>{txCount} txs</strong> | Vol: ₦{Number(volume).toLocaleString()} | Profit: ₦{Number(profit).toLocaleString()}
                </Tag>
              );
            })}
          </Space>
        </Card>
      )}

      {/* Multi-Field Search & Filters */}
      <div style={{ display: "flex", alignItems: "center", justifyContent: "space-between", marginBottom: "16px", gap: "12px", flexWrap: "wrap" }}>
        <div style={{ display: "flex", gap: "12px", flex: 1, minWidth: "300px", flexWrap: "wrap", alignItems: "center" }}>
          <Select
            defaultValue="ALL"
            value={search.type}
            onChange={(val) => {
              setSearch(prev => ({ ...prev, type: val }));
            }}
            style={{ width: 140 }}
            size="large"
            options={[
              { value: "ALL", label: "All Types" },
              { value: "CUSTOMER", label: "Customer" },
              { value: "MERCHANT", label: "Merchant" },
              { value: "CORPORATE", label: "Corporate" },
              { value: "PERSONAL", label: "Personal" },
            ]}
          />

          <Select
            defaultValue="ALL"
            value={search.productCategory}
            onChange={(val) => {
              setSearch(prev => ({ ...prev, productCategory: val }));
            }}
            style={{ width: 170 }}
            size="large"
            options={[
              { value: "ALL", label: "All Products" },
              { value: "UTILITY", label: "Utility" },
              { value: "BILL_PAYMENT", label: "Bill Payment" },
              { value: "FUNDS_TRANSFER", label: "Funds Transfer" },
              { value: "AIRTIME", label: "Airtime / Data" },
              { value: "POS_MERCHANT", label: "POS / Merchant" },
            ]}
          />

          <RangePicker
            size="large"
            format="YYYY-MM-DD"
            onChange={(dates, dateStrings) => {
              const f = dateStrings ? dateStrings[0] : "";
              const t = dateStrings ? dateStrings[1] : "";
              setSearch(prev => ({
                ...prev,
                fromDate: f,
                toDate: t,
              }));
              fetchSummaryAndStats(f, t);
            }}
            style={{ borderRadius: "8px" }}
          />

          <Select
            value={search.sortDir}
            onChange={(val) => {
              setSearch(prev => ({ ...prev, sortDir: val }));
            }}
            style={{ width: 140 }}
            size="large"
            options={[
              { value: "desc", label: "Desc (Newest)" },
              { value: "asc", label: "Asc (Oldest)" },
            ]}
          />

          <Input
            placeholder="Search by Name, Email, Phone, Code, Account, Product..."
            value={search.searchText}
            onChange={(event) => {
              setSearch(prev => ({ ...prev, searchText: event.target.value }));
            }}
            onPressEnter={() => {
              fetchSummaryAndStats();
              getTransactions(0, pagination.pageSize);
            }}
            prefix={<SearchOutlined style={{ color: "#FF4B01" }} />}
            allowClear
            size="large"
            style={{ borderRadius: "8px", flex: 1, minWidth: "220px" }}
          />
        </div>

        <Button
          type="primary"
          onClick={() => {
            fetchSummaryAndStats();
            getTransactions(0, pagination.pageSize);
          }}
          style={{ backgroundColor: "#FF4B01", borderColor: "#FF4B01" }}
          size="large"
        >
          Filter / Refresh
        </Button>
      </div>

      {allTransactions?.length ? (
        <div style={{ height: 650, width: "100%" }}>
          <DataGrid
            getRowId={(row) => row.id}
            className={styles.table}
            density="comfortable"
            rows={
              search.searchText
                ? allTransactions.filter((e) => {
                    const s = search.searchText.trim().toLowerCase();
                    const cleanS = s.replace(/[^0-9a-z]/g, '');
                    const cleanPhone = (e.phoneNumber || '').toString().replace(/[^0-9]/g, '');
                    return (
                      e?.email?.toLowerCase().includes(s) ||
                      e?.name?.toLowerCase().includes(s) ||
                      e?.phoneNumber?.toString().toLowerCase().includes(s) ||
                      (cleanPhone && cleanS && cleanPhone.includes(cleanS)) ||
                      e?.accountNumber?.includes(s) ||
                      e?.customerCategory?.toLowerCase().includes(s)
                    );
                  })
                : allTransactions
            }
            columns={columns}
            page={pagination.current}
            pageSize={pagination.pageSize}
            onPageSizeChange={(newPageSize) => handleChangeRowsPerPage(newPageSize)}
            onPageChange={(newPage) => handleChangePage(newPage)}
            rowsPerPageOptions={[5, 10, 20, 50, 100]}
            pagination
            components={{ Toolbar: GridToolbar }}
            rowCount={pagination.total || allTransactions?.length || 0}
          />
        </div>
      ) : (
        <>
          <Row type="flex" justify="center" align="middle" className="mt-5" style={{ minHeight: "30vh" }}>
            <img src={noDataLogo} alt="nodata" style={{ height: "200px" }} />
          </Row>
          <h4 className="mt-3 text-center">No Onboarded Customers Found</h4>
        </>
      )}

      {/* Customer Product Categories Breakdown Modal */}
      {showProductsModal && (
        <Modal
          title={
            <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
              <AppstoreOutlined style={{ color: '#FF4B01' }} />
              <span>Products & Categories Breakdown: {selectedCustomerForProducts?.name || selectedCustomerForProducts?.email}</span>
            </div>
          }
          visible={showProductsModal}
          onCancel={() => setShowProductsModal(false)}
          width={700}
          footer={[
            <Button key="close" onClick={() => setShowProductsModal(false)}>
              Close
            </Button>,
          ]}
          destroyOnClose
        >
          <Space direction="vertical" style={{ width: '100%' }} size="middle">
            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', backgroundColor: '#f8fafc', padding: '12px', borderRadius: '8px', flexWrap: 'wrap', gap: '8px' }}>
              <div>
                <span>Customer: <strong>{selectedCustomerForProducts?.name}</strong></span>
                <span style={{ marginLeft: 12 }}>Type: <Tag color="purple">{selectedCustomerForProducts?.type}</Tag></span>
                <span style={{ marginLeft: 12 }}>Account: <strong>{selectedCustomerForProducts?.accountNumber || 'N/A'}</strong></span>
              </div>
              <Space>
                <span style={{ fontSize: '12px', color: '#666' }}>Filter Period:</span>
                <RangePicker
                  size="small"
                  format="YYYY-MM-DD"
                  onChange={(dates, dateStrings) => {
                    const f = dateStrings ? dateStrings[0] : "";
                    const t = dateStrings ? dateStrings[1] : "";
                    handleOpenProductsModal(selectedCustomerForProducts, f, t);
                  }}
                  style={{ borderRadius: "6px" }}
                />
              </Space>
            </div>

            <Table
              dataSource={customerProducts}
              columns={productCategoryColumns}
              loading={productsLoading}
              rowKey={(record, idx) => record.category || idx}
              pagination={false}
              size="small"
              bordered
            />
          </Space>
        </Modal>
      )}

      {openModal && <GeneratedQr modalState={openModal} modalCloseAction={modalCloseAction} qrValue={qrData} selectedRow={selectedRow} />}

      {showExport && <ExportData showModal={showExport} hideModal={setShowExport} dataList={allTransactions} />}
    </>
  );
};

export default ManageMarketer;
